import React, { useState } from 'react';

// MUI
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { Add, BookRounded } from '@mui/icons-material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import {
  alpha,
  Button,
  FormControlLabel,
  Grid,
  InputAdornment,
  Select,
  styled,
  Switch,
  TextField,
  Box,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';

//Icons
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { CancelPresentation } from '@mui/icons-material';
import { keyframes } from '@emotion/react';

import TerminateTestSessionsButton from './TerminateTestSessionsButton';
import ConfirmTerminateMultipleSessionsDialog from './ConfirmTerminateMultipleSessionsDialog';
import ConfirmDeleteUnsubmittedSessionsDialog from './ConfirmDeleteUnsubmittedSessionsDialog';
import ConfirmResumeAllPausedDialog from './ConfirmResumeAllPausedDialog';
import useProjectStore from '../../ProjectCanvas/useProjectStore';

const fadeIn = keyframes({
  from: {
    opacity: 0,
  },
  to: {
    opacity: 1,
  },
});

const slideIn = keyframes({
  from: {
    opacity: 0,
    transform: 'translateY(-8px)',
  },
  to: {
    opacity: 1,
    transform: 'translateY(0)',
  },
});

const Outer = styled(Grid)(({ theme, hasSelectedSession }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow: `0 1px 2px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.06)'}`,
  border: `1px solid ${theme.palette.divider}`,
  padding: '14px 16px',
  position: 'sticky',
  top: hasSelectedSession ? '-25px' : 0,
  zIndex: 10,
  animation: `${fadeIn} 0.3s ease-in-out`,
}));

const ButtonRow = styled(Grid)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  alignItems: 'center',
  animation: `${slideIn} 0.3s ease-in-out`,
});

const StyledSpan = styled('span')(({ theme }) => ({
  fontWeight: 600,
  fontSize: '14px',
  marginRight: '4px',
  color: theme.palette.text.primary,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    height: '36px',
    borderRadius: '6px',
    fontSize: '14px',
    transition: 'all 0.2s ease',
    backgroundColor: theme.palette.background.paper,
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.primary,
      borderWidth: '1px',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
  },
  '& .MuiInputAdornment-root': {
    color: theme.palette.text.secondary,
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  fontWeight: 500,
  fontSize: '13px',
  padding: '6px 12px',
  borderRadius: '6px',
  border: `1px solid ${theme.palette.divider}`,
  height: '36px',
  transition: 'all 0.2s ease',
  boxShadow: 'none',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    boxShadow: 'none',
  },
  '&:active': {
    backgroundColor: theme.palette.action.selected,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
    opacity: 0.8,
  },
}));

const TerminateButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.error.main,
  borderColor: theme.palette.error.main,
  '&:hover': {
    backgroundColor: theme.palette.background.paper,
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
  },
  '& .MuiSvgIcon-root': {
    color: theme.palette.error.main,
  },
}));

const SearchButton = styled(ActionButton)({
  minWidth: '90px',
});

const CounterChip = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  height: '36px',
  padding: '0 12px',
  borderRadius: '6px',
  fontSize: '13px',
  backgroundColor: theme.palette.action.hover,
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.secondary,
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase': {
    '&.Mui-checked': {
      color: theme.palette.text.primary,
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.text.secondary,
        opacity: 0.6,
      },
    },
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
  },
}));

function SessionLogsTopBar({
  refreshing,
  refreshSessions,
  searchLogs,
  clearSearchText,
  updateState,
  logsShown,
  logsTotal,
  displayTestData,
  displayUnsubmittedform,
  loadMore,
  projectId,
  searchText,
  selectedSession,
  loadingScreen,
  selectMode = false,
  setSelectMode,
  selectedSessions = [],
  setSelectedSessions,
  sessions = [],
  onTerminateSessions,
  onDeleteUnsubmittedSessions,
  onResumeAllPaused,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [localSearchText, setLocalSearchText] = useState(searchText);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [resumePausedDialogOpen, setResumePausedDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const pausedCount = sessions.filter(session => session.status === 'PAUSED').length;

  // Re-used components
  const RefreshButton = (
    <Tooltip title="Refresh sessions">
      <ActionButton
        size="small"
        disabled={refreshing || loadingScreen}
        startIcon={<AutorenewIcon />}
        onClick={() => refreshSessions(false, displayUnsubmittedform)}
      >
        Refresh
      </ActionButton>
    </Tooltip>
  );

  const LogsShown = (
    <CounterChip>
      <StyledSpan>
        {logsShown}/{logsTotal}
      </StyledSpan>
      <span style={{ fontSize: '13px', color: 'inherit' }}>sessions</span>
    </CounterChip>
  );

  const handleToggleSelectMode = () => {
    setSelectMode(!selectMode);
    if (selectMode) {
      setSelectedSessions([]);
    }
  };

  const handleSelectAll = () => {
    if (selectedSessions.length === sessions.length) {
      setSelectedSessions([]);
    } else {
      setSelectedSessions(sessions.map(session => session.projectSessionId));
    }
  };

  const TerminateSelectedButton = selectMode && selectedSessions.length > 0 && (
    <Tooltip
      title={`Terminate ${selectedSessions.length} selected session${
        selectedSessions.length > 1 ? 's' : ''
      }`}
    >
      <TerminateButton
        size="small"
        onClick={() => setTerminateDialogOpen(true)}
        startIcon={<CancelPresentation />}
      >
        Terminate Sessions ({selectedSessions.length})
      </TerminateButton>
    </Tooltip>
  );

  const handleCopy = () => {
    const idsText = selectedSessions.join(', ');
    navigator.clipboard
      .writeText(idsText)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy session IDs:', err);
      });
  };
  const CopyIDsButton = selectMode && selectedSessions.length > 0 && (
    <Tooltip
      title={copySuccess ? 'Copied to clipboard!' : 'Copy selected session IDs to clipboard'}
    >
      <ActionButton
        size="small"
        onClick={handleCopy}
        startIcon={copySuccess ? <CheckIcon /> : <ContentCopyIcon />}
        sx={
          copySuccess
            ? (theme) => ({
                backgroundColor: alpha(theme.palette.success.main, 0.1),
                color: 'success.dark',
                borderColor: 'success.main',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  borderColor: 'success.main',
                },
              })
            : {}
        }
      >
        {copySuccess ? 'Copied!' : `Copy IDs (${selectedSessions.length})`}
      </ActionButton>
    </Tooltip>
  );

  const ResumeAllPausedButton = !displayTestData && !displayUnsubmittedform && pausedCount > 0 && (
    <Tooltip title="Resume all paused sessions from where they paused">
      <ActionButton
        size="small"
        onClick={() => setResumePausedDialogOpen(true)}
        disabled={refreshing || loadingScreen}
        startIcon={<PlayCircleOutlineIcon />}
      >
        Resume all paused ({pausedCount})
      </ActionButton>
    </Tooltip>
  );

  const SelectModeButton = (
    <Tooltip title={selectMode ? 'Exit select mode' : 'Enter select mode'}>
      <ActionButton
        size="small"
        onClick={handleToggleSelectMode}
        startIcon={selectMode ? <ClearIcon /> : <CheckBoxOutlineBlankIcon />}
      >
        {selectMode ? 'Exit Select' : 'Select'}
      </ActionButton>
    </Tooltip>
  );

  const SelectAllButton = selectMode && (
    <Tooltip title={selectedSessions.length === sessions.length ? 'Deselect all' : 'Select all'}>
      <ActionButton
        size="small"
        onClick={handleSelectAll}
        startIcon={
          selectedSessions.length === sessions.length ? (
            <CheckBoxIcon />
          ) : (
            <CheckBoxOutlineBlankIcon />
          )
        }
      >
        {selectedSessions.length === sessions.length ? 'Deselect All' : 'Select All'}
      </ActionButton>
    </Tooltip>
  );

  const TopButtonRow = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
      <StyledTextField
        id={'searchInput'}
        size={'small'}
        disabled={refreshing || loadingScreen}
        placeholder="Search session logs"
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: localSearchText && (
            <InputAdornment position="end">
              <IconButton
                aria-label="Clear"
                edge="end"
                onClick={() => {
                  setLocalSearchText('');
                  clearSearchText();
                }}
                size="small"
                sx={{
                  padding: '2px',
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary' },
                }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
        variant="outlined"
        value={localSearchText}
        onChange={event => {
          setLocalSearchText(event.target.value);
        }}
        onKeyUp={event => {
          if (event.keyCode === 13) {
            updateState({ searchText: localSearchText });
            document.getElementById('searchButton').click();
          }
        }}
      />
      <SearchButton
        id={'searchButton'}
        size={'small'}
        startIcon={<SearchIcon />}
        disabled={refreshing || loadingScreen}
        onClick={() => {
          updateState({ searchText: localSearchText });
          searchLogs(displayTestData, localSearchText);
        }}
      >
        Search
      </SearchButton>
    </Box>
  );

  const BottomButtonRow = (
    <ButtonRow container>
      {RefreshButton}

      {ResumeAllPausedButton}

      {SelectModeButton}

      {SelectAllButton}

      {TerminateSelectedButton}

      {CopyIDsButton}

      <Tooltip title="View unsubmitted forms">
        <ActionButton
          size="small"
          startIcon={<BookRounded />}
          component={Link}
          to={`/project/unsubmittedForms/${projectId}`}
          target="_blank"
        >
          Unsubmitted forms
        </ActionButton>
      </Tooltip>

      {LogsShown}

      {logsShown < logsTotal && (
        <Tooltip title="Load more session data">
          <ActionButton
            size="small"
            disabled={refreshing || loadingScreen}
            onClick={loadMore}
            startIcon={<Add />}
          >
            Load more
          </ActionButton>
        </Tooltip>
      )}

      <FormControlLabel
        control={
          <StyledSwitch
            checked={displayTestData}
            disabled={refreshing || loadingScreen}
            onChange={() => {
              const updated = !displayTestData;

              const displayTestDataPromise = new Promise((res, rej) => {
                updateState({ displayTestData: updated, selectedSession: null });
                res(updated);
              });

              displayTestDataPromise.then(() => {
                refreshSessions(true);
                searchParams.set('test', updated);
                searchParams.delete('viewSession');
                setSearchParams(searchParams);
              });
            }}
          />
        }
        label={
          <Box sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary' }}>Display test data</Box>
        }
        sx={{ margin: 0 }}
      />

      {displayTestData && (
        <TerminateTestSessionsButton
          projectId={projectId}
          version={useProjectStore.getState().draftVersion}
          onSuccess={() => refreshSessions(true, displayUnsubmittedform)}
        />
      )}
    </ButtonRow>
  );

  const DeleteSelectedButton = selectMode &&
    selectedSessions.length > 0 &&
    displayUnsubmittedform && (
      <Tooltip
        title={`Delete ${selectedSessions.length} selected session${
          selectedSessions.length > 1 ? 's' : ''
        }`}
      >
        <TerminateButton
          size="small"
          onClick={() => setDeleteDialogOpen(true)}
          startIcon={<DeleteIcon />}
        >
          Delete Selected ({selectedSessions.length})
        </TerminateButton>
      </Tooltip>
    );

  const DeleteAllButton = displayUnsubmittedform && sessions.length > 0 && (
    <Tooltip title="Delete all unsubmitted form sessions">
      <TerminateButton
        size="small"
        onClick={() => setDeleteAllDialogOpen(true)}
        startIcon={<DeleteSweepIcon />}
      >
        Delete All ({sessions.length})
      </TerminateButton>
    </Tooltip>
  );

  const fetchTopbarLayout = () => {
    // If it's displaying the un submitted form logic:
    if (displayUnsubmittedform) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {RefreshButton}
            {SelectModeButton}
            {SelectAllButton}
            {DeleteSelectedButton}
            {CopyIDsButton}
            {DeleteAllButton}
            {LogsShown}
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
        {TopButtonRow}
        {BottomButtonRow}
      </Box>
    );
  };

  return (
    <>
      <Outer item xs={12} marginTop="12px" hasSelectedSession={selectedSession}>
        {fetchTopbarLayout()}
      </Outer>

      <ConfirmTerminateMultipleSessionsDialog
        isOpen={terminateDialogOpen}
        close={() => setTerminateDialogOpen(false)}
        sessionIds={selectedSessions}
        terminateSessions={onTerminateSessions}
      />

      <ConfirmDeleteUnsubmittedSessionsDialog
        isOpen={deleteDialogOpen}
        close={() => setDeleteDialogOpen(false)}
        sessionIds={selectedSessions}
        deleteSessions={onDeleteUnsubmittedSessions}
      />

      <ConfirmDeleteUnsubmittedSessionsDialog
        isOpen={deleteAllDialogOpen}
        close={() => setDeleteAllDialogOpen(false)}
        sessionIds={sessions.map(s => s.projectSessionId)}
        deleteSessions={onDeleteUnsubmittedSessions}
        deleteAll={true}
      />

      <ConfirmResumeAllPausedDialog
        isOpen={resumePausedDialogOpen}
        close={() => setResumePausedDialogOpen(false)}
        pausedCount={pausedCount}
        resumeAllPaused={onResumeAllPaused}
      />
    </>
  );
}

export default SessionLogsTopBar;
